import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Shield, Users, DollarSign, Lock, CheckCircle, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const ListPropertyPromo = () => {
  const { isAuthenticated } = useAuth();

  // If user is authenticated, redirect to add listing page
  if (isAuthenticated) {
    return <Navigate to="/add-listing" replace />;
  }

  const benefits = [
    {
      icon: <Shield className="h-6 w-6 text-indigo-600" />,
      title: "Privacy Protected",
      description: "We use smart privacy features to help protect your identity. Property details are only shared with verified users."
    },
    {
      icon: <Users className="h-6 w-6 text-indigo-600" />,
      title: "Verified Renters",
      description: "All potential subletters go through our verification process for your peace of mind."
    },
    {
      icon: <DollarSign className="h-6 w-6 text-indigo-600" />,
      title: "Free to List",
      description: "No upfront costs. List your property for free and only pay when you find a match."
    }
  ];

  const privacyFeatures = [
    "Exact address hidden until matched",
    "Photos automatically scrubbed of identifying details",
    "Private messaging system",
    "Control over listing visibility"
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?ixlib=rb-1.2.1&auto=format&fit=crop&w=2070&q=80" 
            alt="Modern apartment interior" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/90 to-indigo-800/80" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold text-white mb-6">
              Need to Sublease Your Apartment?
            </h1>
            <p className="text-xl text-indigo-100 mb-8">
              Join thousands of others who have found reliable subletters through our platform. 
              List your property in minutes and reach verified renters.
            </p>
            <Link
              to="/register"
              className="inline-block bg-white text-indigo-600 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-indigo-50 transition-colors"
            >
              List Your Property
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Benefits Section */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {benefits.map((benefit, index) => (
            <div key={index} className="bg-white rounded-xl p-6 shadow-sm">
              <div className="mb-4">{benefit.icon}</div>
              <h3 className="text-lg font-semibold mb-2">{benefit.title}</h3>
              <p className="text-gray-600">{benefit.description}</p>
            </div>
          ))}
        </div>

        {/* Privacy Section with Image */}
        <div className="bg-white rounded-xl overflow-hidden shadow-sm mb-16">
          <div className="grid md:grid-cols-2">
            <div className="p-8">
              <div className="flex items-center mb-6">
                <Lock className="h-8 w-8 text-indigo-600 mr-3" />
                <h2 className="text-2xl font-bold">Your Privacy is Our Priority</h2>
              </div>
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">
                  Understanding Subleasing Rights
                </h3>
                <p className="text-gray-600 mb-4">
                  While many leases have provisions about subleasing, the reality is that circumstances change. 
                  Our platform helps you find qualified subletters while maintaining discretion.
                </p>
                <p className="text-gray-600">
                  We recommend reviewing your lease agreement and local regulations. Our platform provides 
                  the tools to help you sublease responsibly and privately.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-4">Privacy Features</h3>
                <ul className="space-y-3">
                  {privacyFeatures.map((feature, index) => (
                    <li key={index} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1554995207-c18c203602cb?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80" 
                alt="Secure apartment" 
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-indigo-900/20 to-transparent" />
            </div>
          </div>
        </div>

        {/* Quick Process Section */}
        <div className="text-center max-w-2xl mx-auto">
          <Clock className="h-12 w-12 text-indigo-600 mx-auto mb-6" />
          <h2 className="text-2xl font-bold mb-4">
            List Your Property in Minutes
          </h2>
          <p className="text-gray-600 mb-8">
            Our streamlined process makes it easy to list your property while maintaining your privacy. 
            Reach thousands of verified potential subletters quickly and securely.
          </p>
          <Link
            to="/register"
            className="inline-block bg-indigo-600 text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-indigo-700 transition-colors"
          >
            Get Started Now
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ListPropertyPromo;